import { Link } from "react-router-dom";
import HeroCarousel from "./components/HeroCarousel";
import MovieCard from "@/components/feature/MovieCard";
import SpecialCard from "@/components/feature/SpecialCard";
import NewsCard from "@/components/feature/NewsCard";
import useSeo from "@/hooks/useSeo";
import { allMovies, allNews, allSpecials } from "@/utils/data";

const categoryEntries = [
  {
    to: "/category?type=电影",
    title: "百合电影",
    desc: "两小时左右的光影，讲完一段心动",
    icon: "ri-film-line",
    count: allMovies.filter((m) => m.type === "电影").length,
  },
  {
    to: "/category?type=电视剧",
    title: "百合电视剧",
    desc: "一周一集的陪伴，慢慢喜欢上一个人",
    icon: "ri-tv-2-line",
    count: allMovies.filter((m) => m.type === "电视剧").length,
  },
];

export default function Home() {
  useSeo({
    title: "百合影视 - 最新百合题材电影电视剧推荐",
    description:
      "百合网站专注于百合题材影视内容，提供丰富的电影、电视剧资讯和详细剧情介绍，满足百合影视爱好者的观影需求。",
    keywords:
      "百合,百合影视,百合电影,百合电视剧,百合剧情,百合推荐,影视资讯",
  });

  const featured = allMovies.filter((m) => m.featured);
  const latest = [...allMovies]
    .sort((a, b) => b.year - a.year || b.rating - a.rating)
    .slice(0, 8);
  const hotNews = allNews.slice(0, 4);

  return (
    <div>
      <HeroCarousel items={featured} />

      <section className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <div className="grid gap-5 md:grid-cols-2">
          {categoryEntries.map((entry) => (
            <Link
              key={entry.to}
              to={entry.to}
              className="group relative flex items-center gap-5 overflow-hidden rounded-lg border border-background-200/70 bg-gradient-to-br from-primary-50 to-accent-50 p-6 transition-colors hover:border-primary-200 md:p-7"
            >
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-2xl text-background-50 md:h-16 md:w-16">
                <i className={entry.icon} />
              </span>
              <div className="min-w-0 flex-1">
                <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                  {entry.title}
                </h2>
                <p className="mt-1.5 text-xs text-foreground-600 md:text-sm">
                  {entry.desc}
                </p>
                <p className="mt-2 text-xs text-primary-600">
                  共 {entry.count} 部作品
                </p>
              </div>
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-background-50 text-foreground-500 transition-all duration-300 group-hover:bg-accent-500 group-hover:text-background-50">
                <i className="ri-arrow-right-line" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 pb-14 md:px-6 md:pb-20">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              最新影视推荐
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              精选近期上线的百合电影与剧集，总有一部戳中你
            </p>
          </div>
          <Link
            to="/category"
            className="hidden shrink-0 items-center gap-1 whitespace-nowrap text-sm text-primary-600 transition-colors hover:text-primary-700 sm:inline-flex"
          >
            查看全部
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-right-line" />
            </span>
          </Link>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-x-5 gap-y-8 sm:grid-cols-3 lg:grid-cols-4">
          {latest.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>

        <div className="mt-8 flex justify-center sm:hidden">
          <Link
            to="/category"
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300/70 px-5 py-2.5 text-sm text-foreground-700"
          >
            查看全部作品
          </Link>
        </div>
      </section>

      <section className="bg-secondary-50 py-14 md:py-20">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <div className="flex items-end justify-between gap-4">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900">
                <i className="ri-bookmark-3-line" />
                编辑精选
              </span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                热门专题合集
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                按主题策展，帮你更快找到对味的那一类故事
              </p>
            </div>
            <Link
              to="/special"
              className="hidden shrink-0 items-center gap-1 whitespace-nowrap text-sm text-primary-600 transition-colors hover:text-primary-700 sm:inline-flex"
            >
              全部专题
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-arrow-right-line" />
              </span>
            </Link>
          </div>

          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {allSpecials.map((special) => (
              <SpecialCard key={special.id} special={special} />
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              影视资讯
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              行业动态、幕后花絮与深度盘点，第一时间掌握
            </p>
          </div>
          <Link
            to="/news"
            className="hidden shrink-0 items-center gap-1 whitespace-nowrap text-sm text-primary-600 transition-colors hover:text-primary-700 sm:inline-flex"
          >
            更多资讯
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-right-line" />
            </span>
          </Link>
        </div>

        <div className="mt-8 grid gap-4 lg:grid-cols-2">
          {hotNews.map((item) => (
            <NewsCard key={item.id} item={item} />
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 pb-4 md:px-6">
        <div className="relative overflow-hidden rounded-lg bg-gradient-to-br from-primary-500 to-accent-500 px-6 py-12 text-center md:px-12 md:py-16">
          <h2 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
            关于百合影视
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-background-50/90 md:text-base">
            我们相信，好的故事不需要被标签定义。百合影视致力于用细腻的分类、真诚的推荐与用心的专题，
            为每一位影迷搭建一个温暖、纯粹的内容空间。愿你在这里，遇见那个让你心动的故事。
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/category"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-6 py-3 text-sm font-medium text-primary-700 transition-transform hover:-translate-y-0.5"
            >
              开始探索
            </Link>
            <Link
              to="/news"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/60 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
            >
              阅读资讯
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}